import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import StopIcon from "@mui/icons-material/Stop";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generatePath, useNavigate } from "react-router-dom";
import apiService, { type MediaChannelAccountCheck, type MediaChannelRow } from "../services/api";
import { ROUTES_NAMES } from "../components/routes";
import { getPodcastImageUrl } from "../utils/imageUtils";

const TYPE_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "tv", label: "TV" },
  { value: "noticias", label: "Noticias" },
  { value: "radio", label: "Radio" },
  { value: "digital", label: "Digital" },
] as const;

const CATEGORY_OPTIONS = [
  { value: "all", label: "All categories" },
  { value: "politics", label: "Politics" },
  { value: "other", label: "Other" },
] as const;

const SOURCE_OPTIONS = [
  { value: "all", label: "All sources" },
  { value: "both", label: "Podcast + livestream" },
  { value: "podcast", label: "Podcast only" },
  { value: "livestream_only", label: "Livestream" },
] as const;

const LIVESTREAM_OPTIONS = [
  { value: "all", label: "Any livestream" },
  { value: "yes", label: "Tracks livestream" },
  { value: "no", label: "Not tracked" },
] as const;

const ACCOUNT_CHECK_OPTIONS = [
  { value: "all", label: "Any check" },
  { value: "unchecked", label: "Not checked" },
  { value: "review", label: "Needs review" },
  { value: "error", label: "Check failed" },
] as const;

const PLATFORM_OPTIONS = [
  { value: "youtube", label: "YouTube" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "tiktok", label: "TikTok" },
  { value: "spotify", label: "Spotify" },
] as const;

type PlatformValue = (typeof PLATFORM_OPTIONS)[number]["value"];

const PLATFORM_LABEL: Record<PlatformValue, string> = Object.fromEntries(
  PLATFORM_OPTIONS.map((opt) => [opt.value, opt.label]),
) as Record<PlatformValue, string>;

function parsePlatformList(value: string): PlatformValue[] {
  const known = new Set<string>(PLATFORM_OPTIONS.map((opt) => opt.value));
  return value.split(",").filter((item): item is PlatformValue => known.has(item));
}

function PlatformMultiSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: PlatformValue[];
  onChange: (next: PlatformValue[]) => void;
}) {
  return (
    <FormControl size="small" sx={{ width: 160, flexShrink: 0 }}>
      <InputLabel>{label}</InputLabel>
      <Select<PlatformValue[]>
        multiple
        label={label}
        value={value}
        onChange={(event) => {
          const next = event.target.value;
          onChange(typeof next === "string" ? parsePlatformList(next) : next);
        }}
        renderValue={(selected) => selected.map((key) => PLATFORM_LABEL[key]).join(", ")}
      >
        {PLATFORM_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <Checkbox size="small" checked={value.includes(opt.value)} sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function SourceChips({ source }: { source: MediaChannelRow["source"] }) {
  if (source === "both") {
    return (
      <Stack direction="row" spacing={0.5}>
        <Chip size="small" label="Podcast" color="primary" variant="outlined" />
        <Chip size="small" label="Livestream" color="success" variant="outlined" />
      </Stack>
    );
  }
  return (
    <Chip
      size="small"
      label={source === "podcast" ? "Podcast" : "Livestream"}
      variant="outlined"
    />
  );
}

function PlatformChips({ row }: { row: MediaChannelRow }) {
  const chips: React.ReactNode[] = [];
  if (row.youtubeChannelId) {
    chips.push(
      <Chip key="yt" size="small" icon={<YouTubeIcon />} label="YT" color="error" variant="outlined" />,
    );
  }
  if (row.instagramUsername) {
    chips.push(<Chip key="ig" size="small" label="IG" color="secondary" variant="outlined" />);
  }
  if (row.facebookUsername) {
    chips.push(<Chip key="fb" size="small" label="FB" color="primary" variant="outlined" />);
  }
  if (row.tiktokUsername) {
    chips.push(<Chip key="tt" size="small" label="TT" variant="outlined" />);
  }
  if (row.spotifyId) {
    chips.push(<Chip key="sp" size="small" label="SP" color="success" variant="outlined" />);
  }
  if (chips.length === 0) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  return <Stack direction="row" spacing={0.5} flexWrap="wrap">{chips}</Stack>;
}

const SHORT_PLATFORM_LABEL: Record<MediaChannelAccountCheck["filled"][number], string> = {
  instagram: "IG",
  facebook: "FB",
  tiktok: "TT",
  spotify: "SP",
};

const SYNC_PLATFORM_LABEL: Record<NonNullable<MediaChannelRow["unsyncedPlatforms"]>[number], string> = {
  youtube: "YT",
  ...SHORT_PLATFORM_LABEL,
};

const REVIEW_REASON: Record<MediaChannelAccountCheck["review"][number]["status"], string> = {
  ambiguous: "YouTube links several accounts",
  taken: "YouTube links an account already used by another channel",
};

function AccountCheckCell({ row }: { row: MediaChannelRow }) {
  const check = row.accountCheck;
  const review = check?.review ?? [];
  const unsynced = row.unsyncedPlatforms ?? [];
  // Nothing left to find: every account is set, or discovery ran and nothing needs review.
  const discoveryDone = (row.accountsComplete || (check != null && !check.error)) && review.length === 0;
  if (discoveryDone) {
    const checkedOn = check ? new Date(check.checkedAt).toLocaleString() : null;
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
        {unsynced.length === 0 ? (
          <Tooltip title={`All accounts found and synced${checkedOn ? ` (checked ${checkedOn})` : ""}`}>
            <CheckCircleOutlineIcon fontSize="small" color="success" />
          </Tooltip>
        ) : (
          <Tooltip title="Open the channel and sync platform data">
            <Typography variant="caption" color="text.secondary">
              Not synced: {unsynced.map((platform) => SYNC_PLATFORM_LABEL[platform]).join(", ")}
            </Typography>
          </Tooltip>
        )}
        {check?.filled.map((platform) => (
          <Tooltip key={`filled-${platform}`} title={`Added from YouTube links on ${checkedOn}`}>
            <Chip size="small" label={`+${SHORT_PLATFORM_LABEL[platform]}`} color="success" variant="outlined" />
          </Tooltip>
        ))}
      </Stack>
    );
  }
  if (!row.youtubeChannelId) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  if (!check) {
    return <Typography variant="caption" color="text.secondary">Not checked</Typography>;
  }
  const checkedOn = new Date(check.checkedAt).toLocaleString();
  if (check.error) {
    return (
      <Tooltip title={`${check.error} (${checkedOn})`}>
        <Chip size="small" label="Failed" color="error" variant="outlined" />
      </Tooltip>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
      {check.filled.map((platform) => (
        <Tooltip key={`filled-${platform}`} title={`Added from YouTube links on ${checkedOn}`}>
          <Chip size="small" label={`+${SHORT_PLATFORM_LABEL[platform]}`} color="success" variant="outlined" />
        </Tooltip>
      ))}
      {check.review.map((item) => (
        <Tooltip
          key={`review-${item.platform}`}
          title={`${REVIEW_REASON[item.status]}${item.value ? `: ${item.value}` : ""}`}
        >
          <Chip size="small" label={`${SHORT_PLATFORM_LABEL[item.platform]}?`} color="warning" variant="outlined" />
        </Tooltip>
      ))}
    </Stack>
  );
}

const ACCOUNT_DISCOVERY_QUERY_KEY = ["admin-media-channels-account-discovery"] as const;
const ACCOUNT_DISCOVERY_POLL_MS = 2000;
const LIST_REFRESH_EVERY = 10;

function AccountDiscoveryPanel({
  activeFilter,
  onFilter,
}: {
  activeFilter: string;
  onFilter: (accountCheck: string) => void;
}) {
  const toggleFilter = (value: string) => onFilter(activeFilter === value ? "all" : value);
  const queryClient = useQueryClient();
  const { data, error } = useQuery({
    queryKey: ACCOUNT_DISCOVERY_QUERY_KEY,
    queryFn: () => apiService.getAdminMediaChannelAccountDiscovery(),
    refetchInterval: (query) => (query.state.data?.job.running ? ACCOUNT_DISCOVERY_POLL_MS : false),
  });
  const job = data?.job;
  const stats = data?.stats;

  // Refresh the table as the job writes, without refetching 250 rows on every poll.
  const processed = job?.processed ?? 0;
  const running = job?.running ?? false;
  const [lastRefreshAt, setLastRefreshAt] = useState(0);
  useEffect(() => {
    if (processed - lastRefreshAt >= LIST_REFRESH_EVERY || (!running && processed !== lastRefreshAt)) {
      setLastRefreshAt(processed);
      void queryClient.invalidateQueries({ queryKey: ["admin-media-channels"] });
    }
  }, [processed, running, lastRefreshAt, queryClient]);

  const startMutation = useMutation({
    mutationFn: (scope: "unchecked" | "all") => apiService.startAdminMediaChannelAccountDiscovery(scope),
    onSuccess: () => {
      setLastRefreshAt(0);
      void queryClient.invalidateQueries({ queryKey: ACCOUNT_DISCOVERY_QUERY_KEY });
    },
  });
  const stopMutation = useMutation({
    mutationFn: () => apiService.stopAdminMediaChannelAccountDiscovery(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ACCOUNT_DISCOVERY_QUERY_KEY }),
  });

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load account discovery progress.
      </Alert>
    );
  }
  if (!stats || !job) {
    return <Skeleton variant="rounded" height={112} sx={{ mb: 2 }} />;
  }

  const unchecked = stats.youtubeChannels - stats.checked - stats.errors;
  const checkedPct = stats.youtubeChannels ? (stats.checked / stats.youtubeChannels) * 100 : 0;
  const jobPct = job.total ? (job.processed / job.total) * 100 : 0;
  const coverage = (["instagram", "facebook", "tiktok", "spotify"] as const).map((platform) => ({
    platform,
    count: stats.platforms[platform],
  }));

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Platform accounts from YouTube links
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {stats.checked} of {stats.youtubeChannels} YouTube channels checked · {stats.filledAccounts} accounts
            added
          </Typography>
          <LinearProgress
            variant="determinate"
            value={running ? jobPct : checkedPct}
            color={running ? "primary" : "success"}
            sx={{ height: 6, borderRadius: 3, mb: 1, bgcolor: "action.selected" }}
          />
          {running ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              {job.stopRequested ? "Stopping after current channel… " : "Running: "}
              {job.processed}/{job.total}
              {job.currentChannel ? ` · ${job.currentChannel}` : ""} · {job.filled} added · {job.needsReview} need
              review · {job.errors} failed
            </Typography>
          ) : job.finishedAt ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              Last run finished {new Date(job.finishedAt).toLocaleString()}: {job.processed}/{job.total} channels,{" "}
              {job.filled} added, {job.needsReview} need review, {job.errors} failed
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`Not checked ${unchecked}`}
              color={activeFilter === "unchecked" ? "primary" : "default"}
              onClick={() => toggleFilter("unchecked")}
              onDelete={activeFilter === "unchecked" ? () => onFilter("all") : undefined}
            />
            <Chip
              size="small"
              label={`Needs review ${stats.needsReview}`}
              color="warning"
              variant={activeFilter === "review" ? "filled" : "outlined"}
              onClick={() => toggleFilter("review")}
              onDelete={activeFilter === "review" ? () => onFilter("all") : undefined}
            />
            <Chip
              size="small"
              label={`Failed ${stats.errors}`}
              color="error"
              variant={activeFilter === "error" ? "filled" : "outlined"}
              onClick={() => toggleFilter("error")}
              onDelete={activeFilter === "error" ? () => onFilter("all") : undefined}
            />
            {coverage.map(({ platform, count }) => (
              <Chip
                key={platform}
                size="small"
                variant="outlined"
                label={`${PLATFORM_LABEL[platform]} ${count}/${stats.totalChannels}`}
              />
            ))}
          </Stack>
        </Box>
        <Stack spacing={1} alignItems="flex-end" sx={{ flexShrink: 0 }}>
          {running ? (
            <Button
              variant="outlined"
              color="error"
              startIcon={<StopIcon />}
              onClick={() => stopMutation.mutate()}
              disabled={job.stopRequested || stopMutation.isPending}
            >
              Stop
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={() => startMutation.mutate("unchecked")}
                disabled={startMutation.isPending || unchecked + stats.errors === 0}
              >
                Check {unchecked + stats.errors} channels
              </Button>
              <Button size="small" onClick={() => startMutation.mutate("all")} disabled={startMutation.isPending}>
                Re-check all {stats.youtubeChannels}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      {startMutation.isError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          Failed to start account discovery.
        </Alert>
      ) : null}
    </Paper>
  );
}

const LIST_PARAM_DEFAULTS = {
  q: "",
  type: "all",
  category: "all",
  source: "all",
  livestream: "all",
  has: "",
  missing: "",
  accountCheck: "all",
  page: "0",
  rows: "250",
} as const;

type ListParam = keyof typeof LIST_PARAM_DEFAULTS;
type ListParams = Record<ListParam, string>;

const LIST_STALE_MS = 5 * 60 * 1000;
const SKELETON_ROWS = 12;
const LIST_PARAMS_STORAGE_KEY = "channels-list-filters";

function loadListParams(): ListParams {
  const params: ListParams = { ...LIST_PARAM_DEFAULTS };
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(LIST_PARAMS_STORAGE_KEY) ?? "{}");
    if (stored && typeof stored === "object") {
      for (const key of Object.keys(LIST_PARAM_DEFAULTS) as ListParam[]) {
        const value = (stored as Record<string, unknown>)[key];
        if (typeof value === "string") params[key] = value;
      }
    }
  } catch {
    // Corrupt or unavailable storage falls back to defaults.
  }
  return params;
}

const MediaChannelsExplorer: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [listParams, setListParams] = useState<ListParams>(loadListParams);
  useEffect(() => {
    try {
      localStorage.setItem(LIST_PARAMS_STORAGE_KEY, JSON.stringify(listParams));
    } catch {
      // Storage full or disabled; filters still work for this session.
    }
  }, [listParams]);
  const param = (key: ListParam) => listParams[key];

  const page = Math.max(0, Number(param("page")) || 0);
  const rowsPerPage = Number(param("rows")) || 250;
  const debouncedSearch = param("q");
  const typeFilter = param("type");
  const categoryFilter = param("category");
  const sourceFilter = param("source");
  const livestreamFilter = param("livestream");
  const hasParam = param("has");
  const missingParam = param("missing");
  const accountCheckFilter = param("accountCheck");
  const hasPlatforms = useMemo(() => parsePlatformList(hasParam), [hasParam]);
  const missingPlatforms = useMemo(() => parsePlatformList(missingParam), [missingParam]);

  const updateParams = useCallback(
    (updates: Partial<Record<ListParam, string>>, options: { resetPage?: boolean } = {}) => {
      setListParams((current) => ({
        ...current,
        ...(options.resetPage !== false ? { page: LIST_PARAM_DEFAULTS.page } : {}),
        ...updates,
      }));
    },
    [],
  );

  const [searchInput, setSearchInput] = useState(debouncedSearch);
  const [createOpen, setCreateOpen] = useState(false);
  const [youtubeChannelId, setYoutubeChannelId] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === debouncedSearch) return;
    const t = setTimeout(() => updateParams({ q: trimmed }), 300);
    return () => clearTimeout(t);
  }, [searchInput, debouncedSearch, updateParams]);

  const listQueryKey = useMemo(
    () =>
      [
        "admin-media-channels",
        page,
        rowsPerPage,
        debouncedSearch,
        typeFilter,
        categoryFilter,
        sourceFilter,
        livestreamFilter,
        hasPlatforms.join(","),
        missingPlatforms.join(","),
        accountCheckFilter,
      ] as const,
    [
      page,
      rowsPerPage,
      debouncedSearch,
      typeFilter,
      categoryFilter,
      sourceFilter,
      livestreamFilter,
      hasPlatforms,
      missingPlatforms,
      accountCheckFilter,
    ],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: listQueryKey,
    queryFn: () =>
      apiService.getAdminMediaChannels({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        type: typeFilter,
        category: categoryFilter,
        source: sourceFilter,
        hasLivestream: livestreamFilter,
        hasPlatforms,
        missingPlatforms,
        accountCheck: accountCheckFilter,
      }),
    staleTime: LIST_STALE_MS,
    placeholderData: keepPreviousData,
  });

  const createMutation = useMutation({
    mutationFn: () => apiService.createAdminMediaChannel(youtubeChannelId.trim()),
    onSuccess: (result) => {
      setCreateOpen(false);
      setYoutubeChannelId("");
      setCreateError(null);
      void queryClient.invalidateQueries({ queryKey: ["admin-media-channels"] });
      navigate(generatePath(ROUTES_NAMES.CHANNEL_DETAIL, { channelId: result.channelId }));
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string; message?: string } } }).response
        ?.data;
      setCreateError(data?.error || data?.message || "Failed to create channel.");
    },
  });

  const openCreate = () => {
    setYoutubeChannelId("");
    setCreateError(null);
    setCreateOpen(true);
  };

  const openChannel = useCallback(
    (row: MediaChannelRow, newTab = false) => {
      const path = generatePath(ROUTES_NAMES.CHANNEL_DETAIL, { channelId: row._id });
      if (newTab) {
        window.open(path, "_blank", "noopener");
        return;
      }
      navigate(path);
    },
    [navigate],
  );

  const channels = data?.channels ?? [];
  const total = data?.pagination.total ?? 0;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Channels
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {total || "—"} channels. Edits update the channels collection only.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Create channel
          </Button>
          <IconButton
            onClick={() => {
              void refetch();
              void queryClient.invalidateQueries({ queryKey: ACCOUNT_DISCOVERY_QUERY_KEY });
            }}
            disabled={isFetching}
            aria-label="Refresh"
          >
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      <AccountDiscoveryPanel
        activeFilter={accountCheckFilter}
        onFilter={(accountCheck) => updateParams({ accountCheck })}
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ overflowX: "auto", pt: 0.75, pb: 0.25 }}>
          <TextField
            size="small"
            placeholder="Search name, slug, YouTube ID, handles..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 220, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ width: 140, flexShrink: 0 }}>
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={typeFilter} onChange={(e) => updateParams({ type: e.target.value })}>
              {TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 140, flexShrink: 0 }}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={categoryFilter}
              onChange={(e) => updateParams({ category: e.target.value })}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 140, flexShrink: 0 }}>
            <InputLabel>Source</InputLabel>
            <Select
              label="Source"
              value={sourceFilter}
              onChange={(e) => updateParams({ source: e.target.value })}
            >
              {SOURCE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 140, flexShrink: 0 }}>
            <InputLabel>Livestream</InputLabel>
            <Select
              label="Livestream"
              value={livestreamFilter}
              onChange={(e) => updateParams({ livestream: e.target.value })}
            >
              {LIVESTREAM_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <PlatformMultiSelect
            label="Has platforms"
            value={hasPlatforms}
            onChange={(next) =>
              updateParams({
                has: next.join(","),
                missing: missingPlatforms.filter((key) => !next.includes(key)).join(","),
              })
            }
          />
          <PlatformMultiSelect
            label="Missing platforms"
            value={missingPlatforms}
            onChange={(next) =>
              updateParams({
                missing: next.join(","),
                has: hasPlatforms.filter((key) => !next.includes(key)).join(","),
              })
            }
          />
          <FormControl size="small" sx={{ width: 150, flexShrink: 0 }}>
            <InputLabel>Accounts check</InputLabel>
            <Select
              label="Accounts check"
              value={accountCheckFilter}
              onChange={(e) => updateParams({ accountCheck: e.target.value })}
            >
              {ACCOUNT_CHECK_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load channels.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ position: "relative", overflow: "hidden" }}>
        {isFetching && !isLoading ? (
          <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 2 }} />
        ) : null}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Channel</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Platforms</TableCell>
                <TableCell>Accounts check</TableCell>
                <TableCell>Livestream</TableCell>
                <TableCell>Programs</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: SKELETON_ROWS }, (_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Skeleton variant="circular" width={36} height={36} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="60%" />
                          <Skeleton variant="text" width="35%" sx={{ fontSize: 12 }} />
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="rounded" width={56} height={22} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Skeleton variant="rounded" width={34} height={22} />
                        <Skeleton variant="rounded" width={34} height={22} />
                        <Skeleton variant="rounded" width={34} height={22} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="rounded" width={40} height={22} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={60} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="rounded" width={70} height={22} />
                    </TableCell>
                  </TableRow>
                ))
              ) : channels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No channels found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                channels.map((row) => (
                  <TableRow
                    key={row._id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={(e) => openChannel(row, e.metaKey || e.ctrlKey)}
                    onAuxClick={(e) => {
                      if (e.button === 1) openChannel(row, true);
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={getPodcastImageUrl({ imageS3Key: row.imageS3Key })}
                          alt={row.name}
                          sx={{ width: 36, height: 36 }}
                        />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.slug}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.type} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <PlatformChips row={row} />
                    </TableCell>
                    <TableCell>
                      <AccountCheckCell row={row} />
                    </TableCell>
                    <TableCell>
                      {row.hasLivestream ? (
                        <Chip
                          size="small"
                          label={row.trackLivestream ? "Tracked" : "Not tracked"}
                          color={row.trackLivestream ? "success" : "default"}
                          variant="outlined"
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {row.podcastProgramCount > 0 ||
                      row.livestreamProgramCount > 0 ||
                      row.channelProgramCount > 0 ? (
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {row.channelProgramCount > 0 ? (
                            <Chip
                              size="small"
                              color="secondary"
                              label={`Channel ${row.channelProgramCount}`}
                            />
                          ) : null}
                          {row.podcastProgramCount > 0 ? (
                            <Chip
                              size="small"
                              label={`Podcast ${row.podcastProgramCount}`}
                              variant="outlined"
                            />
                          ) : null}
                          {row.livestreamProgramCount > 0 ? (
                            <Chip
                              size="small"
                              label={`Live ${row.livestreamProgramCount}`}
                              variant="outlined"
                            />
                          ) : null}
                        </Stack>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <SourceChips source={row.source} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => updateParams({ page: String(next) }, { resetPage: false })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => updateParams({ rows: String(parseInt(e.target.value, 10)) })}
          rowsPerPageOptions={[50, 100, 250]}
        />
      </Paper>

      <Dialog
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create channel</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {createError ? <Alert severity="error">{createError}</Alert> : null}
            <TextField
              label="YouTube channel"
              value={youtubeChannelId}
              onChange={(event) => setYoutubeChannelId(event.target.value)}
              fullWidth
              required
              size="small"
              autoFocus
              placeholder="UC…, @handle, or channel URL"
              helperText="Creates the legacy podcast and the legacy livestream channel from this YouTube channel."
              onKeyDown={(event) => {
                if (event.key === "Enter" && youtubeChannelId.trim() && !createMutation.isPending) {
                  createMutation.mutate();
                }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !youtubeChannelId.trim()}
          >
            {createMutation.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MediaChannelsExplorer;
